import React from 'react';
import RevealPasswordModal from '../../../Modal/RevealPasswordModal/RevealPasswordModal';
import { connect } from 'react-redux';
import { Box, Tooltip } from '@mui/material';
import CopyToClipboard from '../CopyToClipboardCell';
import {
  getAllPermissionRolesForUserLevel,
  PERMISSIONROLECONSTANTS,
  ROLECONSTANTS,
} from '../../../../features/utils/utils.action';
import CurrentStatusDot from '../../../Other/CurrentStatusDot/CurrentStatusDot';
import { ADDRESS_STATUS, CURRENT_STATUS, ORG_LEVEL, statusNames } from '../../../../utils/constants';
import { formatRouterSignalMetricValue, SignalStatus } from '../../../../utils/SignalsStatus';
import SignalDot from '../../../Other/SignalDot/SignalDot';
import {
  isSuperadminOrTelcloudAdmin,
  parseCounterToString,
  parseCounterToStringShort,
} from '../../../../utils/utilities';
import VisibilityGuard from '../../../../hocs/VisibilityGuard';
import { ROUTER_TYPE } from '../../../../types/enums/router-type.enum';
import { setModalContent, setShowModal } from '../../../../features/utils/utils.slice';
import { differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds } from 'date-fns';
import Hint from '../../../Other/Hint/Hint';
import AppLink from '../../../Other/AppLink/AppLink';
import { formatDate, formatDateTime } from '../../../../utils/dateUtils';
import JsonView from '@uiw/react-json-view';

const CustomCell = props => {
  const dismissModal = () => {
    props.setShowModal(false);
    props.setModalContent(null);
  };

  const showPasswordModal = item => e => {
    e.stopPropagation();
    props.setModalContent(<RevealPasswordModal password={item} onClose={dismissModal} />);
    props.setShowModal(true);
  };

  const renderTableCell = () => {
    switch (props.type) {
      case 'userStatus':
        return (
          <>
            {props.value === 'offline' && (
              <Tooltip title='Offline' arrow>
                <i className='font-12 fa-solid fa-circle text-secondary' />
              </Tooltip>
            )}
            {props.value === 'online' && (
              <Tooltip title='Online' arrow>
                <i className='font-12 fa-solid fa-circle text-green' />
              </Tooltip>
            )}
            {props.value === 'inactive' && (
              <Tooltip title='Suspended' arrow>
                <i className='font-12 fa-solid fa-circle text-danger' />
              </Tooltip>
            )}
          </>
        );
      case 'dateTime':
        return <>{props.value ? formatDateTime(props.value) : <span className='text-muted'>-</span>}</>;
      case 'seconds':
        return <>{props.value != null ? `${props.value} s` : <span className='text-muted'>-</span>}</>;
      case 'date':
        return <>{props.value ? formatDate(props.value) : <span className='text-muted'>-</span>}</>;
      case 'durationAGo':
        return (
          <div className={'d-flex'} style={props.color ? { color: props.color } : undefined}>
            {props.value ? (
              <Tooltip title={formatDateTime(props.value)} arrow>
                <div>
                  {parseCounterToStringShort({
                    days: differenceInDays(new Date(props.value), new Date()),
                    hours: differenceInHours(new Date(props.value), new Date()) % 24,
                    minutes: differenceInMinutes(new Date(props.value), new Date()) % 60,
                    seconds: differenceInSeconds(new Date(props.value), new Date()) % 60,
                  })}
                </div>
              </Tooltip>
            ) : (
              <div>None</div>
            )}
          </div>
        );
      case 'durationToGo':
        return (
          <div className={'d-flex'} style={props.color ? { color: props.color } : undefined}>
            {props.value ? (
              <Tooltip title={formatDateTime(props.value)} arrow>
                <div>
                  {differenceInDays(new Date(props.value), Date.now())}d{' '}
                  {differenceInHours(new Date(props.value), Date.now()) % 24}h{' '}
                  {differenceInMinutes(new Date(props.value), Date.now()) % 60}m{' '}
                  {differenceInSeconds(new Date(props.value), Date.now()) % 60}s left
                </div>
              </Tooltip>
            ) : (
              <div>None</div>
            )}
          </div>
        );
      case 'durationSimple':
        return (
          <div className='d-flex' style={props.color ? { color: props.color } : undefined}>
            {props.value ? (
              <Tooltip title={formatDateTime(props.value)} arrow>
                <div>
                  {differenceInDays(new Date(props.value), Date.now())}d{' '}
                  {differenceInHours(new Date(props.value), Date.now()) % 24}h{' '}
                  {differenceInMinutes(new Date(props.value), Date.now()) % 60}m
                </div>
              </Tooltip>
            ) : (
              <div>None</div>
            )}
          </div>
        );
      case 'boolean':
        return (
          <div
            className={'form-inline'}
            style={{ position: 'relative', marginTop: '4px', color: props.domainData?.main_color }}
          >
            {props.value ? (
              <i className='fa fa-check-circle text-info'></i>
            ) : (
              <i className='fa-regular fa-circle text-secondary'></i>
            )}
          </div>
        );
      case 'currentStatusDot':
        return (
          <CurrentStatusDot
            value={props.value ?? CURRENT_STATUS.UNDEFINED}
            tooltipTitle={props.tooltipTitle ?? statusNames[CURRENT_STATUS.UNDEFINED]}
          />
        );
      case 'link':
        return (
          <p>
            <AppLink rel={props.rel} target={props.target} to={props.link} className='list-link'>
              {props.value}
            </AppLink>
            <span style={{ fontSize: '10px' }}>{props.valueType && ` ${props.valueType}`}</span>
          </p>
        );
      case 'address':
        return (
          <div className={'d-flex'}>
            {props.value}
            {props.status !== ADDRESS_STATUS.DELIVERABLE && props.warning && (
              <Hint
                icon={props.status === ADDRESS_STATUS.DELIVERABLE_WITH_ERROR ? 'warning' : 'error'}
                title={props.warning}
                className='ml-1'
              />
            )}
          </div>
        );
      case 'isActive':
        return (
          <div className={'d-flex'}>
            {props.value}
            {props.warning && (
              <Hint icon='warning' title={props.message ? props.message : 'Inactive'} className='ml-1' />
            )}
          </div>
        );
      case 'imei':
        return (
          <div className={'d-flex flex-column'}>
            {props.value.map(i => (
              <div key={i.id}>{i.imei}</div>
            ))}
          </div>
        );
      case 'payload':
        return (
          <JsonView
            value={JSON.parse(props.value || {})}
            collapsed={true}
            enableClipboard={false}
            displayDataTypes={false}
          />
        );
      case 'remove':
        return (
          <div className={'d-flex justify-content-end'}>
            <VisibilityGuard allowedRoles={props.allowedRoles || []}>
              <Tooltip title='Remove' arrow>
                <div className={'delete-icon pointer'}>
                  <i className={'fa fa-trash'} style={{ pointerEvents: 'none' }} />
                </div>
              </Tooltip>
            </VisibilityGuard>
          </div>
        );
      case 'edit':
        return (
          <div className={'d-flex justify-content-end'}>
            <VisibilityGuard allowedRoles={props.allowedRoles || []}>
              <Tooltip title='Edit' placement='top-end' arrow>
                <div className={'edit-icon pointer'}>
                  <i className={'fa-solid fa-pen-to-square'} style={{ pointerEvents: 'none' }} />
                </div>
              </Tooltip>
            </VisibilityGuard>
          </div>
        );
      case 'password':
        return (
          <div className={' d-flex'} style={{ position: 'relative' }}>
            ***********{' '}
            <Tooltip title='Reveal password' arrow>
              <div className={'ml-1'} onClick={showPasswordModal(props.value)}>
                <i className={'fa fa-eye'}></i>
              </div>
            </Tooltip>
          </div>
        );
      case 'signal_ethernet': {
        const { name, connection_state } = props.router_interface;
        const dateType = props.status === CURRENT_STATUS.ONLINE ? 'Online duration:' : 'Offline duration:';
        let currentStatus = CURRENT_STATUS.UNDEFINED;
        if (connection_state === 'connected') currentStatus = CURRENT_STATUS.ONLINE;
        if (connection_state === 'disconnected') currentStatus = CURRENT_STATUS.OFFLINE;

        return (
          <div className={`d-flex align-items-center index-${props.index}`}>
            {props.index}:
            <Tooltip
              title={
                <div>
                  <div className={'mb-1'}>{`Interface ${props.index} ${name && `(${name})`}`}</div>
                  <div>{dateType}</div>
                  <div>
                    {props.status_updated_at
                      ? parseCounterToString({
                          days: differenceInDays(new Date(props.status_updated_at), new Date()),
                          hours: differenceInHours(new Date(props.status_updated_at), new Date()) % 24,
                          minutes: differenceInMinutes(new Date(props.status_updated_at), new Date()) % 60,
                        })
                      : 'Never online'}
                  </div>
                </div>
              }
              arrow
            >
              <div className={'ml-1 mr-2'}>
                <i className={`fa fa-ethernet index-${props.index}`}></i>
              </div>
            </Tooltip>
            <Tooltip
              title={
                <div>
                  <div className={'mb-1'}>{`Status: ${connection_state || statusNames[CURRENT_STATUS.UNDEFINED]}`}</div>
                  <div>{dateType}</div>
                  <div>
                    {props.status_updated_at
                      ? parseCounterToString({
                          days: differenceInDays(new Date(props.status_updated_at), new Date()),
                          hours: differenceInHours(new Date(props.status_updated_at), new Date()) % 24,
                          minutes: differenceInMinutes(new Date(props.status_updated_at), new Date()) % 60,
                        })
                      : 'Never online'}
                  </div>
                </div>
              }
              arrow
            >
              <div className={'mr-1'}>
                <CurrentStatusDot value={currentStatus} />
              </div>
            </Tooltip>
          </div>
        );
      }
      case 'signal': {
        const { name, rssi, sinr, rsrq, rsrp } = props.router_interface;
        const dateType = props.status === CURRENT_STATUS.ONLINE ? 'Online duration:' : 'Offline duration:';
        return (
          <div className={`d-flex align-items-center signal index-${props.index}`}>
            {props.index}:
            <Tooltip
              title={
                <div>
                  <div className={'mb-1'}>{`Interface ${props.index} ${name && `(${name})`}`}</div>
                  <div>{dateType}</div>
                  <div>
                    {props.status_updated_at
                      ? parseCounterToString({
                          days: differenceInDays(new Date(props.status_updated_at), new Date()),
                          hours: differenceInHours(new Date(props.status_updated_at), new Date()) % 24,
                          minutes: differenceInMinutes(new Date(props.status_updated_at), new Date()) % 60,
                        })
                      : 'Never online'}
                  </div>
                </div>
              }
              arrow
            >
              <div className={'ml-1 mr-2'}>
                <i className={`fa fa-wifi signal index-${props.index}`}></i>
              </div>
            </Tooltip>
            <Tooltip
              title={
                <div>
                  <div className={'mb-1'}>{`RSSI: ${formatRouterSignalMetricValue(rssi)}`}</div>
                  <div>{dateType}</div>
                  <div>
                    {props.status_updated_at
                      ? parseCounterToString({
                          days: differenceInDays(new Date(props.status_updated_at), new Date()),
                          hours: differenceInHours(new Date(props.status_updated_at), new Date()) % 24,
                          minutes: differenceInMinutes(new Date(props.status_updated_at), new Date()) % 60,
                        })
                      : 'Never online'}
                  </div>
                </div>
              }
              arrow
            >
              <div className={'mr-1'}>
                <SignalDot value={SignalStatus.rssi(rssi)} index={props.index} />
              </div>
            </Tooltip>
            <Tooltip
              title={
                <div>
                  <div className={'mb-1'}>{`SINR: ${formatRouterSignalMetricValue(sinr)}`}</div>
                  <div>{dateType}</div>
                  <div>
                    {props.status_updated_at
                      ? parseCounterToString({
                          days: differenceInDays(new Date(props.status_updated_at), new Date()),
                          hours: differenceInHours(new Date(props.status_updated_at), new Date()) % 24,
                          minutes: differenceInMinutes(new Date(props.status_updated_at), new Date()) % 60,
                        })
                      : 'Never online'}
                  </div>
                </div>
              }
              arrow
            >
              <div className={'mr-1'}>
                <SignalDot value={SignalStatus.sinr(sinr)} index={props.index} />
              </div>
            </Tooltip>
            <Tooltip
              title={
                <div>
                  <div className={'mb-1'}>{`RSRQ: ${formatRouterSignalMetricValue(rsrq)}`}</div>
                  <div>{dateType}</div>
                  <div>
                    {props.status_updated_at
                      ? parseCounterToString({
                          days: differenceInDays(new Date(props.status_updated_at), new Date()),
                          hours: differenceInHours(new Date(props.status_updated_at), new Date()) % 24,
                          minutes: differenceInMinutes(new Date(props.status_updated_at), new Date()) % 60,
                        })
                      : 'Never online'}
                  </div>
                </div>
              }
              arrow
            >
              <div className={'mr-1'}>
                <SignalDot value={SignalStatus.rsrq(rsrq)} index={props.index} />
              </div>
            </Tooltip>
            <Tooltip
              title={
                <div>
                  <div className={'mb-1'}>{`RSRP: ${formatRouterSignalMetricValue(rsrp)}`}</div>
                  <div>{dateType}</div>
                  <div>
                    {props.status_updated_at
                      ? parseCounterToString({
                          days: differenceInDays(new Date(props.status_updated_at), new Date()),
                          hours: differenceInHours(new Date(props.status_updated_at), new Date()) % 24,
                          minutes: differenceInMinutes(new Date(props.status_updated_at), new Date()) % 60,
                        })
                      : 'Never online'}
                  </div>
                </div>
              }
              arrow
            >
              <div className={'mr-1'}>
                <SignalDot value={SignalStatus.rsrp(rsrp)} index={props.index} />
              </div>
            </Tooltip>
          </div>
        );
      }
      case 'orgActions': {
        return (
          <div className={'d-flex justify-content-end'}>
            <Tooltip title='Edit' arrow>
              <div className={'edit-icon pointer'}>
                <i className={'fa fa-pen'} style={{ pointerEvents: 'none' }} />
              </div>
            </Tooltip>
            {props.is_open && (
              <Tooltip title='Remove' arrow>
                <div className={'delete-icon pointer '} style={{ marginLeft: '5px' }}>
                  <i className={'fa fa-trash'} style={{ pointerEvents: 'none' }} />
                </div>
              </Tooltip>
            )}
          </div>
        );
      }
      case 'warningName': {
        return !props.allow ? (
          `${props.value}`
        ) : (
          <div className={'d-flex'}>
            {props.value}
            {props.warnings.length > 0 && (
              <>
                {props.warnings.map(warning =>
                  warning.show ? (
                    <div key={warning.message} className={'revio-icon pointer'}>
                      <Tooltip title={warning.message} arrow>
                        <span>
                          <Box>
                            <i
                              className={`${warning.icon || 'fas fa-exclamation-triangle'} ${
                                warning.color || 'text-danger'
                              } mx-1`}
                            />
                          </Box>
                        </span>
                      </Tooltip>
                    </div>
                  ) : null
                )}
                {props.showFix && (
                  <div className={'fix-icon pointer'}>
                    <Tooltip title={'Fix'} arrow>
                      <div className={'fix-icon'}>
                        <i className='fas fa-wrench' style={{ pointerEvents: 'none' }} />
                      </div>
                    </Tooltip>
                  </div>
                )}
                {props.showRefresh && (
                  <div className={'ml-1 refresh-icon pointer'}>
                    <Tooltip title={'Refresh links'} arrow>
                      <div className={'refresh-icon'}>
                        <i className='fas fa-sync-alt' style={{ pointerEvents: 'none' }} />
                      </div>
                    </Tooltip>
                  </div>
                )}
              </>
            )}
          </div>
        );
      }
      case 'pbxActions': {
        const allowRemove = !(props.isSkySwitchDomain && props.user.role !== ROLECONSTANTS.superadmin);
        return (
          <VisibilityGuard
            allowedRole={[
              PERMISSIONROLECONSTANTS.superadmin,
              ...getAllPermissionRolesForUserLevel('telcloud', ['admin']),
              ...getAllPermissionRolesForUserLevel('reseller', ['owner', 'admin']),
            ]}
          >
            <div className={'d-flex justify-content-end'}>
              {allowRemove && (
                <>
                  <Tooltip title='Edit' arrow>
                    <div className={'edit-icon pointer'}>
                      <i className={'fa fa-pen'} style={{ pointerEvents: 'none' }} />
                    </div>
                  </Tooltip>
                  <Tooltip title='Remove' arrow>
                    <div className={'delete-icon pointer '} style={{ marginLeft: '5px' }}>
                      <i className={'fa fa-trash'} style={{ pointerEvents: 'none' }} />
                    </div>
                  </Tooltip>
                </>
              )}
            </div>
          </VisibilityGuard>
        );
      }
      case 'routerActions': {
        const allowRemove = props.routerType === ROUTER_TYPE.CUSTOM || props.routerType === ROUTER_TYPE.PENDING;
        return (
          <div className={'d-flex justify-content-start'}>
            {isSuperadminOrTelcloudAdmin(props.user) && (
              <div className={'refresh-icon pointer'}>
                <Tooltip title='Update Status' arrow>
                  <div className={'refresh-icon'}>
                    <i className='fas fa-sync-alt' style={{ pointerEvents: 'none' }} />
                  </div>
                </Tooltip>
              </div>
            )}
            <div className={'edit-icon pointer'}>
              <Tooltip title='Edit' arrow>
                <div className={'edit-icon'}>
                  <i className={'fa fa-pen'} style={{ pointerEvents: 'none', marginLeft: '5px' }} />
                </div>
              </Tooltip>
            </div>
            {allowRemove && isSuperadminOrTelcloudAdmin(props.user) && (
              <div className={'delete-icon pointer '}>
                <Tooltip title='Delete' arrow>
                  <div className={'delete-icon '}>
                    <i className={'fa fa-trash'} style={{ pointerEvents: 'none', marginLeft: '5px' }} />
                  </div>
                </Tooltip>
              </div>
            )}
          </div>
        );
      }
      case 'orgName':
        return (
          <div className={'d-flex justify-content-start'}>
            <p>
              {`${props.value} `}
              {props.level === ORG_LEVEL.RESELLER && (
                <span
                  className={'font-weight-bold mr-1'}
                  style={{
                    color: 'white',
                    backgroundColor: '#667287',
                    borderRadius: '2px',
                    padding: '0px 3px',
                  }}
                >
                  P
                </span>
              )}
              {props.level === ORG_LEVEL.SUB_ORG && (
                <span
                  className={'font-weight-bold mr-1'}
                  style={{
                    color: 'white',
                    backgroundColor: '#667287',
                    borderRadius: '2px',
                    padding: '0px 3px',
                  }}
                >
                  S
                </span>
              )}
              {props.level === ORG_LEVEL.SUB_ORG_1 && (
                <span
                  className={'font-weight-bold mr-1'}
                  style={{
                    color: 'white',
                    backgroundColor: '#667287',
                    borderRadius: '2px',
                    padding: '0px 3px',
                  }}
                >
                  G
                </span>
              )}
              {props.isBobo && (
                <span
                  className={'font-weight-bold mr-1'}
                  style={{
                    color: 'white',
                    backgroundColor: '#667287',
                    borderRadius: '2px',
                    padding: '0px 3px',
                  }}
                >
                  B
                </span>
              )}

              {props.revio_customer_id_is_missing && (
                <Hint icon='error' title='Organization without Rev.io ID' className='edit-icon pointer mr-1' />
              )}
              {props.revio_bobo_customer_id_is_missing && (
                <Hint icon='error' title='Organization without BoBo ID' className='edit-icon pointer mr-1' />
              )}
              {props.level === ORG_LEVEL.RESELLER && props.domain && (
                <>
                  <VisibilityGuard allowedRoles={[PERMISSIONROLECONSTANTS.superadmin]}>
                    <span className={'redirect-icon pointer mr-1'}>
                      <Tooltip title={`Go to ${props.domain} page`} arrow>
                        <span className={'redirect-icon'}>
                          <i className='fa-solid fa-globe' style={{ pointerEvents: 'none' }}></i>
                        </span>
                      </Tooltip>
                    </span>
                  </VisibilityGuard>
                </>
              )}
            </p>
          </div>
        );
      case 'didObject':
        return (
          <div className={'d-flex flex-column'}>
            <div>
              {props?.value?.did.length > 0 && props?.copyAvailable && <CopyToClipboard value={props.value?.did} />}
              {(props.value.did || '').toUpperCase()}{' '}
              {props.value?.customer_owned ? (
                <span
                  className={'font-weight-bold mr-1'}
                  style={{
                    color: 'white',
                    backgroundColor: '#667287',
                    borderRadius: '2px',
                    padding: '0px 3px',
                  }}
                >
                  CO
                </span>
              ) : (
                ''
              )}
              {props.value?.is_temporary ? (
                <span
                  className={'font-weight-bold mr-1'}
                  style={{
                    color: 'white',
                    backgroundColor: '#667287',
                    borderRadius: '2px',
                    padding: '0px 3px',
                  }}
                >
                  T
                </span>
              ) : (
                ''
              )}
            </div>
            {props?.value?.temporary_did && (
              <div>
                {props?.value?.temporary_did && props?.copyAvailable && (
                  <CopyToClipboard value={props.value?.temporary_did?.did} />
                )}
                {(props.value?.temporary_did?.did || '').toUpperCase()}{' '}
                <span
                  className={'font-weight-bold mr-1'}
                  style={{
                    color: 'white',
                    backgroundColor: '#667287',
                    borderRadius: '2px',
                    padding: '0px 3px',
                  }}
                >
                  T
                </span>
              </div>
            )}
          </div>
        );
      case 'report':
        return (
          <div>
            <span className={'mr-2'}>{props.value}</span>
            {props.value > 0 ? (
              <span>
                <i className='fa-solid fa-xmark text-danger mr-2' />
                <span style={{ cursor: 'pointer' }} className={'revio-report'}>
                  Show Report
                </span>
              </span>
            ) : (
              <i className='fa-solid fa-check text-success' />
            )}
          </div>
        );

      case 'string':
      default: {
        const isEmpty = props.value == null || (typeof props.value === 'string' && props.value.trim() === '');
        if (isEmpty) {
          return <span className='text-muted'>-</span>;
        }
        return (
          <div
            className={`d-flex ${
              props.textAlign === 'center'
                ? 'justify-content-center pr-3'
                : props.textAlign === 'right'
                ? 'justify-content-end pr-3'
                : ''
            }`}
          >
            {props?.value?.length > 0 && props?.copyAvailable && <CopyToClipboard value={props.value} />}
            <span style={props.color ? { color: props.color } : undefined}>
              {props?.toUpper ? props.value.toUpperCase() : props.value}
            </span>
          </div>
        );
      }
    }
  };
  return <>{renderTableCell()}</>;
};

const mapStateToProps = state => {
  return {
    user: state.authReducer.user,
  };
};
const mapDispatchToProps = dispatch => ({
  setModalContent: (value, size) => {
    dispatch(setModalContent({ content: value, size }));
  },
  setShowModal: value => {
    dispatch(setShowModal(value));
  },
});
export default React.memo(connect(mapStateToProps, mapDispatchToProps)(CustomCell));
CustomCell.whyDidYouRender = true;
